import { useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { SiteEvidenceFlag } from '../../lib/employerSiteEvidenceFlags';

type Props = {
  flag: SiteEvidenceFlag | null;
};

/** Enough copies that the strip stays filled on a wide dialog. */
const COPIES = 4;

/** Seconds for one copy to travel the strip. Slow enough to read in passing. */
const LAP_SECONDS = 18;

export function EmployerSiteEvidenceBulletin({ flag }: Props) {
  // Reduced-motion operators get the message standing still until they press
  // play, rather than losing the ticker entirely.
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [paused, setPaused] = useState(reducedMotion);

  if (!flag) return null;

  const warning = flag.severity === 'warning';

  return (
    <Box
      data-testid="employer-site-evidence-bulletin"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 0.5,
        borderBottom: '1px solid',
        borderColor: (t) => alpha(warning ? t.palette.warning.main : t.palette.info.main, 0.45),
        bgcolor: (t) =>
          alpha(
            warning ? t.palette.warning.main : t.palette.info.main,
            t.palette.mode === 'light' ? 0.14 : 0.24,
          ),
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          flexShrink: 0,
          px: 0.85,
          py: 0.2,
          borderRadius: 0.75,
          bgcolor: (t) => (warning ? t.palette.warning.main : t.palette.info.main),
          color: (t) =>
            t.palette.getContrastText(warning ? t.palette.warning.main : t.palette.info.main),
        }}
      >
        {warning ? (
          <WarningAmberRoundedIcon sx={{ fontSize: 14 }} />
        ) : (
          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 900, letterSpacing: 0.6, fontSize: '0.65rem' }}
        >
          IMAGERY DATE
        </Typography>
      </Stack>

      <Box
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          // Fade the edges so text appears to enter and leave the strip.
          maskImage:
            'linear-gradient(to right, transparent, black 24px, black calc(100% - 24px), transparent)',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            willChange: 'transform',
            animation: `employer-bulletin-scroll ${LAP_SECONDS * COPIES}s linear infinite`,
            animationPlayState: paused ? 'paused' : 'running',
            '@keyframes employer-bulletin-scroll': {
              from: { transform: 'translateX(0)' },
              // One copy of the message equals a seamless lap.
              to: { transform: `translateX(-${100 / COPIES}%)` },
            },
          }}
        >
          {Array.from({ length: COPIES }, (_, copy) => (
            <Stack
              key={copy}
              aria-hidden={copy > 0}
              direction="row"
              alignItems="center"
              spacing={0.6}
              sx={{ px: 2 }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: warning ? 'warning.dark' : 'text.primary',
                }}
              >
                {flag.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {flag.detail}
              </Typography>
              <Box
                aria-hidden
                sx={{
                  width: 4,
                  height: 4,
                  ml: 1,
                  borderRadius: '50%',
                  bgcolor: 'text.disabled',
                }}
              />
            </Stack>
          ))}
        </Box>
      </Box>

      <IconButton
        size="small"
        onClick={() => setPaused((p) => !p)}
        aria-label={paused ? 'Resume imagery date bulletin' : 'Pause imagery date bulletin'}
        sx={{ flexShrink: 0, p: 0.4 }}
      >
        {paused ? (
          <PlayArrowRoundedIcon sx={{ fontSize: 16 }} />
        ) : (
          <PauseRoundedIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
    </Box>
  );
}
